import { useState } from "react";
import { Check, Phone, MessageSquare, ShieldCheck, Wallet } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { CARS, CONTACT } from "@/lib/data";

function CarCard({ car, index }) {
  const [active, setActive] = useState(0);
  return (
    <Reveal delay={index * 0.1}>
      <div className="group rounded-[1.75rem] border border-navy/10 bg-white shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-500">
        <div className="relative aspect-[4/3] bg-secondary overflow-hidden">
          <img
            src={car.images[active]}
            alt={car.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          />
          <span className="absolute top-4 left-4 bg-white/90 backdrop-blur rounded-full px-3 py-1 text-navy text-xs font-bold uppercase tracking-wider border border-navy/10">
            {car.badge}
          </span>
          <div className="absolute top-4 right-4 bg-navy rounded-full px-4 py-2 shadow-lg flex items-baseline gap-1">
            <span className="font-heading font-extrabold text-gold text-lg">${car.price}</span>
            <span className="text-white/70 text-xs font-semibold">/ day</span>
          </div>
        </div>

        {car.images.length > 1 && (
          <div className="flex gap-2 p-4 pb-0 overflow-x-auto">
            {car.images.map((img, i) => (
              <button
                key={i}
                data-testid={`car-${car.id}-thumb-${i}`}
                onClick={() => setActive(i)}
                aria-label={`View ${car.name} photo ${i + 1}`}
                className={`h-16 w-20 rounded-lg overflow-hidden border-2 shrink-0 transition-colors ${
                  i === active ? "border-gold" : "border-transparent opacity-70 hover:opacity-100"
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="p-6 md:p-8">
          <h3 className="font-heading font-bold text-navy text-2xl md:text-3xl tracking-tight">{car.name}</h3>
          <p className="text-gold-dark font-semibold text-sm mt-1">{car.tagline}</p>

          <ul className="mt-6 grid sm:grid-cols-2 gap-3">
            {car.specs.map((s) => (
              <li key={s} className="flex items-start gap-2 text-navy/80 text-sm">
                <Check className="w-4 h-4 text-gold-dark mt-0.5 shrink-0" /> {s}
              </li>
            ))}
          </ul>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a
              href="#book"
              data-testid={`car-${car.id}-reserve`}
              onClick={() => window.dispatchEvent(new CustomEvent("orl:selectCar", { detail: car.id }))}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-white font-bold text-sm hover:bg-navy-light hover:-translate-y-0.5 transition-all duration-300 shadow-lg"
            >
              <Phone className="w-4 h-4" /> Reserve — ${car.price}/day
            </a>
            <a
              href={`sms:${CONTACT.phoneRaw}`}
              data-testid={`car-${car.id}-text`}
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-navy/20 px-6 py-3.5 text-navy font-semibold text-sm hover:border-gold hover:text-gold-dark transition-all duration-300"
            >
              <MessageSquare className="w-4 h-4" /> Text to Book
            </a>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export const Fleet = () => (
  <section id="fleet" data-testid="fleet-section" className="bg-secondary py-24 md:py-36">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal className="max-w-2xl mb-14">
        <p className="text-gold-dark text-xs font-bold uppercase tracking-[0.3em] mb-4">Our Fleet</p>
        <h2 className="font-heading font-bold text-navy text-4xl sm:text-5xl lg:text-[3.4rem] tracking-tight leading-[1.02]">
          Choose your car.
        </h2>
        <p className="mt-5 text-muted-foreground text-base md:text-lg leading-relaxed">
          Pick the ride that fits your hustle and your budget. Every rental includes maintenance and
          200 miles per day — no credit checks, no hidden fees. Rentals stay in Central Florida:
          Orange, Osceola, Lake, Seminole &amp; Sumter counties.
        </p>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-8">
        {CARS.map((car, i) => (
          <CarCard key={car.id} car={car} index={i} />
        ))}
      </div>

      <Reveal delay={0.1}>
        <div
          data-testid="coverage-note"
          className="mt-8 rounded-2xl border border-gold/40 bg-gold/10 px-6 py-5 flex items-start gap-4"
        >
          <ShieldCheck className="w-6 h-6 text-gold-dark shrink-0 mt-0.5" />
          <p className="text-navy text-base md:text-lg leading-relaxed">
            <span className="font-heading font-bold">Coverage included.</span> Every rental includes coverage and
            maintenance, so your daily rate stays simple.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div
          data-testid="deposit-note"
          className="mt-4 rounded-2xl border border-navy/10 bg-white px-6 py-5 flex items-start gap-4"
        >
          <Wallet className="w-6 h-6 text-gold-dark shrink-0 mt-0.5" />
          <p className="text-navy text-base md:text-lg leading-relaxed">
            <span className="font-heading font-bold">Refundable deposit.</span> $100 for every rental —
            applied toward tolls and incidentals, and fully returned to you if it isn't used.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
